/**
 * Not Found Page - The Intelligent Standard
 *
 * Elegant 404 page matching the ArenaHome aesthetic with
 * red hero section, flowing curves, and clean typography.
 */

import { Link } from 'react-router-dom';

import { BrokenLogo } from '../components/BrokenLogo';

export default function NotFoundPage() {
  return (
    <div>
      {/* Styles */}
      <style>{`
        @keyframes numberReveal {
          from { 
            opacity: 0;
            transform: translateY(8px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes gentleFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        @keyframes gentleFloat2 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(12px); }
        }
        @keyframes gentleFloat3 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .wave-primary {
          animation: gentleFloat 25s ease-in-out infinite;
        }
        .wave-secondary {
          animation: gentleFloat2 30s ease-in-out infinite;
        }
        .wave-tertiary {
          animation: gentleFloat3 22s ease-in-out infinite;
        }
        .number-reveal {
          animation: numberReveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
          opacity: 0;
        }
        @keyframes fade-up {
          from { 
            opacity: 0;
            transform: translateY(16px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* ========== HERO SECTION (RED) ========== */}
      <section
        className="relative overflow-hidden min-h-screen"
        style={{
          background:
            'linear-gradient(165deg, #9A2B3C 0%, #8B2332 20%, #6B1A27 60%, #4A1219 100%)',
        }}
      >
        {/* Grid pattern */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ opacity: 0.05 }}
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <pattern
              id="notFoundGrid"
              width="80"
              height="80"
              patternUnits="userSpaceOnUse"
            >
              <rect
                width="80"
                height="80"
                fill="none"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.8"
              />
              <line
                x1="26.66"
                y1="0"
                x2="26.66"
                y2="80"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.4"
              />
              <line
                x1="53.33"
                y1="0"
                x2="53.33"
                y2="80"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.4"
              />
              <line
                x1="0"
                y1="20"
                x2="80"
                y2="20"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.25"
              />
              <line
                x1="0"
                y1="40"
                x2="80"
                y2="40"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.25"
              />
              <line
                x1="0"
                y1="60"
                x2="80"
                y2="60"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.25"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#notFoundGrid)" />
        </svg>

        {/* Flowing mathematical curves */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 1400 800"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
        >
          <defs>
            <linearGradient
              id="flowGradient404_1"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="white" stopOpacity="0">
                <animate
                  attributeName="offset"
                  values="-0.5;1"
                  dur="6s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="15%" stopColor="white" stopOpacity="0.25">
                <animate
                  attributeName="offset"
                  values="-0.35;1.15"
                  dur="6s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="30%" stopColor="white" stopOpacity="0">
                <animate
                  attributeName="offset"
                  values="-0.2;1.3"
                  dur="6s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>

            <linearGradient
              id="flowGradient404_2"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="white" stopOpacity="0">
                <animate
                  attributeName="offset"
                  values="-0.5;1"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="20%" stopColor="white" stopOpacity="0.2">
                <animate
                  attributeName="offset"
                  values="-0.3;1.2"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="40%" stopColor="white" stopOpacity="0">
                <animate
                  attributeName="offset"
                  values="-0.1;1.4"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>
          </defs>

          {/* Base curves - static, very subtle */}
          <path
            className="wave-primary"
            d="M-200 120 Q 200 80, 500 140 Q 800 200, 1100 120 Q 1400 40, 1600 100"
            stroke="white"
            strokeWidth="1"
            strokeOpacity="0.04"
            fill="none"
          />
          <path
            className="wave-secondary"
            d="M-200 220 Q 150 280, 450 220 Q 750 160, 1050 240 Q 1350 320, 1600 260"
            stroke="white"
            strokeWidth="1"
            strokeOpacity="0.03"
            fill="none"
          />
          <path
            className="wave-tertiary"
            d="M-200 380 Q 200 320, 500 400 Q 800 480, 1100 380 Q 1400 280, 1600 360"
            stroke="white"
            strokeWidth="1.5"
            strokeOpacity="0.05"
            fill="none"
          />
          <path
            className="wave-primary"
            d="M-200 520 Q 250 600, 550 520 Q 850 440, 1150 540 Q 1450 640, 1600 580"
            stroke="white"
            strokeWidth="1"
            strokeOpacity="0.04"
            fill="none"
          />

          {/* Animated flow overlays */}
          <path
            className="wave-primary"
            d="M-200 120 Q 200 80, 500 140 Q 800 200, 1100 120 Q 1400 40, 1600 100"
            stroke="url(#flowGradient404_1)"
            strokeWidth="2"
            fill="none"
          />
          <path
            className="wave-tertiary"
            d="M-200 380 Q 200 320, 500 400 Q 800 480, 1100 380 Q 1400 280, 1600 360"
            stroke="url(#flowGradient404_2)"
            strokeWidth="2.5"
            fill="none"
          />
        </svg>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center px-6 min-h-screen">
          {/* Broken Logo */}
          <div className="animate-fade-up">
            <BrokenLogo size={80} />
          </div>

          {/* 404 Number */}
          <div
            className="font-serif font-normal tracking-tight mb-2 number-reveal -mt-2"
            style={{
              color: '#FFFFFF',
              fontSize: 'clamp(4rem, 15vw, 8rem)',
              lineHeight: 1,
              letterSpacing: '-0.02em',
              animationDelay: '100ms',
            }}
          >
            404
          </div>

          {/* Label */}
          <div
            className="text-xs uppercase tracking-widest mb-8 number-reveal"
            style={{ color: 'rgba(255,255,255,0.45)', animationDelay: '200ms' }}
          >
            Page Not Found
          </div>

          {/* Description */}
          <p
            className="text-base max-w-md mx-auto text-center mb-6 animate-fade-up"
            style={{
              color: 'rgba(255, 255, 255, 0.65)',
              animationDelay: '250ms',
            }}
          >
            The page you're looking for doesn't exist or has been moved.
          </p>

          {/* CTA Button */}
          <div className="animate-fade-up" style={{ animationDelay: '300ms' }}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
              style={{
                background: 'rgba(255,255,255,0.95)',
                color: '#8B2332',
                border: '1px solid white',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 3L3 8L8 13M3.5 8H13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Return Home
            </Link>
          </div>

          {/* Contact link */}
          <div
            className="animate-fade-up mt-8"
            style={{ animationDelay: '400ms' }}
          >
            <span
              className="text-xs"
              style={{ color: 'rgba(255,255,255,0.4)' }}
            >
              Need help?{' '}
              <a
                href="mailto:contact@spreadsheetarena.ai"
                className="transition-colors hover:text-white"
                style={{ color: 'rgba(255,255,255,0.6)' }}
              >
                contact@spreadsheetarena.ai
              </a>
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
